import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { CashIncentiveClaimService } from '../../services/cashIncentiveClaim.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
    ICashIncentiveClaim, IUpdateCashIncentiveClaimStatusRequest, IncentiveClaimStatus,
} from '../../interfaces/commercial.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../utils/dateUtils';

const STATUS_COLOR: Record<IncentiveClaimStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default', SUBMITTED: 'info', UNDER_AUDIT: 'info', APPROVED: 'success', RECEIVED: 'success', REJECTED: 'error',
};

const money = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// §5.3, fully manual (CashIncentiveClaimService.ALLOWED_TRANSITIONS, verbatim) -- never invented:
// DRAFT -> SUBMITTED -> UNDER_AUDIT -> APPROVED -> RECEIVED, REJECTED reachable from
// SUBMITTED/UNDER_AUDIT/APPROVED; RECEIVED and REJECTED are terminal.
const NEXT_STATUSES: Record<IncentiveClaimStatus, IncentiveClaimStatus[]> = {
    DRAFT: ['SUBMITTED'],
    SUBMITTED: ['UNDER_AUDIT', 'REJECTED'],
    UNDER_AUDIT: ['APPROVED', 'REJECTED'],
    APPROVED: ['RECEIVED', 'REJECTED'],
    RECEIVED: [],
    REJECTED: [],
};

const TRANSITION_LABEL: Record<IncentiveClaimStatus, string> = {
    DRAFT: 'Draft', SUBMITTED: 'Submit', UNDER_AUDIT: 'Send to Audit', APPROVED: 'Approve',
    RECEIVED: 'Mark Received', REJECTED: 'Reject',
};

// §5.5: soft-delete allowed only in DRAFT/REJECTED (CashIncentiveClaimService.DELETABLE_STATUSES)
// -- a claim past SUBMITTED is a filed document.
const DELETABLE_STATUSES: IncentiveClaimStatus[] = ['DRAFT', 'REJECTED'];

const today = () => toLocalIsoDate();

/**
 * R-3: cash-incentive claims are the module's one LDC-sunset-risk gate. When
 * `commercial_settings.cash_incentive_enabled` is false the register comes back with
 * `enabled: false` and a named `disabledReason` -- this screen renders that as an info banner and
 * nothing else, never an error and never a silently-empty table with no explanation.
 */
const CashIncentiveClaimList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.CASH_INCENTIVE_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<ICashIncentiveClaim[]>([]);
    const [total, setTotal] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [disabledReason, setDisabledReason] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<IncentiveClaimStatus | ''>('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const [transitionTarget, setTransitionTarget] = useState<{ claim: ICashIncentiveClaim; to: IncentiveClaimStatus } | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [receivedAmount, setReceivedAmount] = useState('');
    const [receivedDate, setReceivedDate] = useState(today());
    const [deleteTarget, setDeleteTarget] = useState<ICashIncentiveClaim | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [correctTarget, setCorrectTarget] = useState<ICashIncentiveClaim | null>(null);
    const [correctAmount, setCorrectAmount] = useState('');
    const [aitAmount, setAitAmount] = useState(''); // W3-B7
    const [correctAit, setCorrectAit] = useState('');
    const [correctDate, setCorrectDate] = useState('');
    const [correctRemarks, setCorrectRemarks] = useState('');

    const [rateTarget, setRateTarget] = useState<ICashIncentiveClaim | null>(null);
    const [rateValue, setRateValue] = useState('');
    const [rateReason, setRateReason] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            const res = await CashIncentiveClaimService.list(institutionId, page, size, status);
            setEnabled(res.enabled);
            setDisabledReason(res.disabledReason);
            setRows(res.claims.content);
            setTotal(res.claims.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load cash-incentive claims');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, status]); // eslint-disable-line react-hooks/exhaustive-deps

    const openTransition = (claim: ICashIncentiveClaim, to: IncentiveClaimStatus) => {
        setRejectionReason('');
        setReceivedAmount(''); setAitAmount('');
        setReceivedDate(today());
        setTransitionTarget({ claim, to });
    };

    const settledTotal = (Number(receivedAmount) || 0) + (Number(aitAmount) || 0);
    const settledExceedsClaim = transitionTarget?.to === 'RECEIVED'
        && settledTotal > (Number(transitionTarget.claim.claimedAmount) || 0) + 0.005;

    const transitionValid = () => {
        if (!transitionTarget) return false;
        if (transitionTarget.to === 'REJECTED') return rejectionReason.trim().length > 0;
        if (transitionTarget.to === 'RECEIVED') return receivedAmount !== '' && Number(receivedAmount) >= 0;
        return true;
    };

    const transitionWhy = submitting ? null
        : transitionTarget?.to === 'REJECTED' && rejectionReason.trim().length === 0 ? 'Enter the rejection reason.'
        : transitionTarget?.to === 'RECEIVED' && (receivedAmount === '' || Number(receivedAmount) < 0) ? 'Enter the received amount, zero or more.'
        : null;

    const confirmTransition = async () => {
        if (!transitionTarget || !transitionValid()) return;
        const { claim, to } = transitionTarget;
        const req: IUpdateCashIncentiveClaimStatusRequest = { status: to };
        if (to === 'REJECTED') req.rejectionReason = rejectionReason.trim();
        if (to === 'RECEIVED') { req.receivedAmount = Number(receivedAmount); req.receivedDate = receivedDate; req.aitAmount = aitAmount === '' ? null : Number(aitAmount); }
        try {
            setSubmitting(true);
            const updated = await CashIncentiveClaimService.updateStatus(institutionId, claim.id, req);
            if (updated.postingWarning) toast.showWarning(updated.postingWarning);
            else toast.showSuccess(`Claim ${claim.claimNumber} moved to ${to}`);
            setTransitionTarget(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to change claim status');
        } finally {
            setSubmitting(false);
        }
    };

    /** Only a posting that was attempted and failed is retried; historical claims never offer it. */
    const needsLedgerPost = (c: ICashIncentiveClaim) => c.ledgerPostingPending === true;

    const handlePostLedger = async (claim: ICashIncentiveClaim) => {
        try {
            await CashIncentiveClaimService.postToLedger(institutionId, claim.id);
            toast.showSuccess(`Claim ${claim.claimNumber} posted to the ledger`);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to post the claim to the ledger');
        }
    };

    const openCorrect = (claim: ICashIncentiveClaim) => {
        setCorrectAmount(claim.receivedAmount != null ? String(claim.receivedAmount) : '');
        setCorrectAit(claim.aitAmount ? String(claim.aitAmount) : '');
        setCorrectDate(claim.receivedDate ?? today());
        setCorrectRemarks(claim.remarks ?? '');
        setCorrectTarget(claim);
    };

    const correctValid = correctAmount !== '' && Number(correctAmount) >= 0 && !!correctDate
        && correctDate <= today()
        && (!correctTarget?.submittedDate || correctDate >= correctTarget.submittedDate);

    const fixWhy = submitting ? null
        : correctAmount === '' || Number(correctAmount) < 0 ? 'Enter the corrected received amount, zero or more.'
        : !correctDate ? 'Enter the receipt date.'
        : correctDate > today() ? 'The receipt date cannot be in the future.'
        : !!correctTarget?.submittedDate && correctDate < correctTarget.submittedDate ? 'The receipt date cannot be before the submitted date.'
        : null;

    const confirmCorrect = async () => {
        if (!correctTarget || !correctValid) return;
        try {
            setSubmitting(true);
            const corrected = await CashIncentiveClaimService.correctReceipt(institutionId, correctTarget.id, {
                receivedAmount: Number(correctAmount), receivedDate: correctDate, remarks: correctRemarks.trim() || null,
                aitAmount: correctAit === '' ? null : Number(correctAit),
            });
            if (corrected.postingWarning) toast.showWarning(corrected.postingWarning);
            else toast.showSuccess(`Receipt of claim ${correctTarget.claimNumber} corrected`);
            setCorrectTarget(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to correct receipt');
        } finally {
            setSubmitting(false);
        }
    };

    const openRate = (claim: ICashIncentiveClaim) => {
        setRateValue(String(claim.ratePercent));
        setRateReason('');
        setRateTarget(claim);
    };

    const rateNum = Number(rateValue);
    const rateValid = rateValue !== '' && rateNum > 0 && rateNum <= 20 && rateReason.trim().length > 0;
    const ratePreview = rateTarget && rateValue !== '' && rateNum > 0
        ? Math.round(rateTarget.realizedAmountBdt * rateNum) / 100 : null;

    const rateWhy = submitting ? null
        : rateValue === '' || !(rateNum > 0) || rateNum > 20 ? 'Enter a rate above 0 and up to 20.'
        : rateReason.trim().length === 0 ? 'Enter the reason for the change.'
        : null;

    const confirmRate = async () => {
        if (!rateTarget || !rateValid) return;
        try {
            setSubmitting(true);
            await CashIncentiveClaimService.correctRate(institutionId, rateTarget.id, {
                ratePercent: rateNum, reason: rateReason.trim(),
            });
            toast.showSuccess(`Rate of claim ${rateTarget.claimNumber} corrected`);
            setRateTarget(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to correct rate');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setSubmitting(true);
            await CashIncentiveClaimService.remove(institutionId, deleteTarget.id);
            toast.showSuccess(`Claim ${deleteTarget.claimNumber} deleted`);
            setDeleteTarget(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to delete claim');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    if (!enabled) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>Cash Incentive Claims</Typography>
                <Alert severity="info" data-testid="cash-incentive-disabled-banner">
                    {disabledReason ?? 'Cash incentive claims are disabled for this institution.'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.claims.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Cash Incentive Claims</Typography>
                {canManage && (
                    <Button data-help="commercial.claims.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/cash-incentive/new')}>
                        New Claim
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.claims.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                    data-help="commercial.claims.statusFilter" select label="Status" size="small" value={status}
                    onChange={(e) => { setStatus(e.target.value as IncentiveClaimStatus | ''); setPage(0); }}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="DRAFT">Draft</MenuItem>
                    <MenuItem value="SUBMITTED">Submitted</MenuItem>
                    <MenuItem value="UNDER_AUDIT">Under audit</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="RECEIVED">Received</MenuItem>
                    <MenuItem value="REJECTED">Rejected</MenuItem>
                </TextField>
            </Box>

            <TableContainer component={Paper} data-help-section="commercial.claims.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="commercial.claims.col.number">Claim Number</TableCell>
                            <TableCell data-help="commercial.claims.col.bank">Bank</TableCell>
                            <TableCell align="right" data-help="commercial.claims.col.rate">Rate %</TableCell>
                            <TableCell align="right" data-help="commercial.claims.col.amount">Claimed Amount</TableCell>
                            <TableCell data-help="commercial.claims.col.status">Status</TableCell>
                            {canManage && <TableCell align="right" data-help="commercial.claims.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.claimNumber}</TableCell>
                                <TableCell>{r.bankName}</TableCell>
                                <TableCell align="right">{r.ratePercent}</TableCell>
                                {/*
                                  claimedAmount is ALWAYS BDT (CashIncentiveClaimService#computeClaimedAmount
                                  derives it from realizedAmountBdt, never from r.currencyCode -- the DTO's
                                  currencyCode field is the underlying EXP's OWN currency, e.g. USD, paired
                                  with realizedAmountBase, a different field entirely). Labelling this column
                                  with r.currencyCode would render a BDT figure under a USD tag -- exactly the
                                  mismatched-currency confusion the "no bare numeric column" rule exists to
                                  prevent, just inverted. "BDT" is the correct, literal label here.
                                */}
                                <TableCell align="right" data-testid={`claim-amount-${r.id}`}>BDT {money(r.claimedAmount)}</TableCell>
                                <TableCell><Chip data-help="commercial.claims.status" size="small" label={enumLabel(r.status)} color={STATUS_COLOR[r.status]} /></TableCell>
                                {canManage && (
                                    <TableCell align="right">
                                        {NEXT_STATUSES[r.status].map(to => (
                                            <Button data-help="commercial.claims.transition" key={to} size="small" onClick={() => openTransition(r, to)}
                                                color={to === 'REJECTED' ? 'error' : 'primary'}>
                                                {TRANSITION_LABEL[to]}
                                            </Button>
                                        ))}
                                        {needsLedgerPost(r) && (
                                            <Button data-help="commercial.claims.postLedger" size="small" color="warning" onClick={() => handlePostLedger(r)}
                                                data-testid={`claim-post-ledger-${r.id}`}>Post to ledger</Button>
                                        )}
                                        {r.status === 'RECEIVED' && (
                                            <Button data-help="commercial.claims.correctReceipt" size="small" onClick={() => openCorrect(r)}>Correct receipt</Button>
                                        )}
                                        {(r.status === 'DRAFT' || r.status === 'SUBMITTED') && (
                                            <Button data-help="commercial.claims.correctRate" size="small" onClick={() => openRate(r)}>Correct rate</Button>
                                        )}
                                        {DELETABLE_STATUSES.includes(r.status) && (
                                            <Button data-help="commercial.claims.delete" size="small" color="warning" onClick={() => setDeleteTarget(r)}>Delete</Button>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No claims found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                />
            </TableContainer>

            <Dialog open={!!transitionTarget} onClose={() => setTransitionTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {transitionTarget && `${TRANSITION_LABEL[transitionTarget.to]} — ${transitionTarget.claim.claimNumber}`}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {transitionTarget?.to === 'REJECTED' && (
                        <TextField data-help="commercial.claims.rejectReason" label="Rejection Reason" required multiline minRows={2}
                            value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
                    )}
                    {transitionTarget?.to === 'RECEIVED' && (
                        <>
                            <TextField data-help="commercial.claims.receivedAmount" label="Received amount (cash credited by the bank)" type="number" required
                                value={receivedAmount} onChange={e => setReceivedAmount(e.target.value)} />
                            <TextField data-help="commercial.claims.ait" label="Tax deducted at source (AIT)" type="number" value={aitAmount}
                                helperText="Optional. In addition to the amount received; needs the source-tax account in Commercial settings."
                                onChange={e => setAitAmount(e.target.value)} />
                            <Typography variant="body2" data-testid="incentive-total-settled">
                                Total settled = received + AIT = BDT {money(settledTotal)}
                            </Typography>
                            {settledExceedsClaim && (
                                <Alert severity="warning">
                                    Received + AIT (BDT {money(settledTotal)}) is more than the claimed amount
                                    (BDT {money(transitionTarget.claim.claimedAmount)}). Check both figures.
                                </Alert>
                            )}
                            <TextField data-help="commercial.claims.receivedDate" label="Received Date" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                                inputProps={{ min: transitionTarget.claim.submittedDate ?? undefined }}
                                helperText={transitionTarget.claim.submittedDate ? `Not before the submitted date (${transitionTarget.claim.submittedDate})` : undefined}
                                value={receivedDate} onChange={e => setReceivedDate(e.target.value)} />
                        </>
                    )}
                    {transitionTarget && transitionTarget.to !== 'REJECTED' && transitionTarget.to !== 'RECEIVED' && (
                        <DialogContentText>
                            Move claim '{transitionTarget.claim.claimNumber}' to {transitionTarget.to}?
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.claims.transitionCancel" onClick={() => setTransitionTarget(null)}>Cancel</Button>
                    <Button data-help="commercial.claims.transitionConfirm" {...helpWhy(transitionWhy ? { kind: 'status', message: transitionWhy } : null)} variant="contained" disabled={submitting || !transitionValid()} onClick={confirmTransition}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!correctTarget} onClose={() => setCorrectTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{correctTarget && `Correct receipt — ${correctTarget.claimNumber}`}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField data-help="commercial.claims.fixAmount" label="Received Amount" type="number" required
                        value={correctAmount} onChange={e => setCorrectAmount(e.target.value)} />
                    <TextField data-help="commercial.claims.fixAit" label="Tax deducted at source (AIT)" type="number" value={correctAit}
                        onChange={e => setCorrectAit(e.target.value)} />
                    <TextField data-help="commercial.claims.fixDate" label="Received Date" type="date" required InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        inputProps={{ max: today(), min: correctTarget?.submittedDate ?? undefined }}
                        value={correctDate} onChange={e => setCorrectDate(e.target.value)} />
                    <TextField data-help="commercial.claims.fixRemarks" label="Remarks" multiline minRows={2}
                        value={correctRemarks} onChange={e => setCorrectRemarks(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.claims.fixCancel" onClick={() => setCorrectTarget(null)}>Cancel</Button>
                    <Button data-help="commercial.claims.fixSave" {...helpWhy(fixWhy ? { kind: 'status', message: fixWhy } : null)} variant="contained" disabled={submitting || !correctValid} onClick={confirmCorrect}>
                        Save correction
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!rateTarget} onClose={() => setRateTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{rateTarget && `Correct rate — ${rateTarget.claimNumber}`}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField data-help="commercial.claims.rateValue" label="Rate (%)" type="number" required
                        inputProps={{ min: 0, max: 20, step: 0.01 }}
                        helperText="Greater than 0, at most 20"
                        value={rateValue} onChange={e => setRateValue(e.target.value)} />
                    <TextField data-help="commercial.claims.rateReason" label="Reason" required multiline minRows={2}
                        inputProps={{ maxLength: 300 }}
                        value={rateReason} onChange={e => setRateReason(e.target.value)} />
                    {rateTarget && (
                        <DialogContentText data-testid="rate-preview">
                            Claimed amount: BDT {money(rateTarget.claimedAmount)} {'→'} BDT {ratePreview != null ? money(ratePreview) : '—'}
                            {' '}(on realized BDT {money(rateTarget.realizedAmountBdt)})
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.claims.rateCancel" onClick={() => setRateTarget(null)}>Cancel</Button>
                    <Button data-help="commercial.claims.rateSave" {...helpWhy(rateWhy ? { kind: 'status', message: rateWhy } : null)} variant="contained" disabled={submitting || !rateValid} onClick={confirmRate}>
                        Save rate
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete claim?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently delete claim '{deleteTarget?.claimNumber}'. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.claims.deleteCancel" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button data-help="commercial.claims.deleteConfirm" color="warning" disabled={submitting} onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CashIncentiveClaimList;
